import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import './CalendarioCampanas.css';

// ─── Config ───────────────────────────────────────────────────────────────────
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;

// ─── Constantes ───────────────────────────────────────────────────────────────
const OBJECTIVES = ['Sales', 'Engagement', 'Retention', 'Acquisition', 'Winback', 'Loyalty', 'Other'];
const CHANNELS = ['Email', 'Web', 'SMS', 'Push', 'Social', 'All'];

const OBJECTIVE_COLORS = {
  Sales: '#2563eb', Engagement: '#7c3aed', Retention: '#059669',
  Acquisition: '#d97706', Winback: '#dc2626', Loyalty: '#0891b2', Other: '#6b7280',
};

const FECHAS_ESPECIALES = [
  { nombre: "Mother's Day", fecha: '2026-05-10', categoria: 'Gifts' },
  { nombre: "Father's Day", fecha: '2026-06-21', categoria: 'Gifts' },
  { nombre: 'Black Friday', fecha: '2026-11-27', categoria: 'General' },
  { nombre: 'Cyber Monday', fecha: '2026-11-30', categoria: 'General' },
  { nombre: 'Christmas', fecha: '2026-12-24', categoria: 'General' },
  { nombre: 'Halloween', fecha: '2026-10-31', categoria: 'Season' },
  { nombre: "Valentine's Day", fecha: '2026-02-14', categoria: 'Gifts' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function diasRestantes(fecha) {
  const hoy = new Date();
  const d = new Date(fecha + 'T00:00:00');
  return Math.ceil((d - hoy) / (1000 * 60 * 60 * 24));
}

function getEstado(entry) {
  const hoy = new Date();
  const inicio = new Date(entry.date_start + 'T00:00:00');
  const fin = new Date(entry.date_end + 'T00:00:00');
  if (hoy < inicio) return 'proxima';
  if (hoy > fin) return 'terminada';
  return 'activa';
}

function mockDelay(ms = 600) { return new Promise(r => setTimeout(r, ms)); }

// ─── Gemini: interpretar Excel ─────────────────────────────────────────────────
async function interpretarExcelConGemini(textoPlano) {
  const prompt = `Eres un asistente de marketing. Analiza este archivo Excel de campañas de una tienda.
Devuelve SOLO un JSON válido con este formato exacto (sin markdown):
{
  "calendar_name": "nombre descriptivo del calendario",
  "entries": [
    {
      "date_start": "YYYY-MM-DD",
      "date_end": "YYYY-MM-DD", 
      "objective": "Sales|Engagement|Retention|Acquisition|Winback|Loyalty|Other",
      "concept": "nombre de la campaña",
      "discounts": "descripción del descuento u oferta",
      "channels": ["Email","Web","SMS","Push","Social","All"],
      "promo_code": "código si existe o vacío",
      "content_suggestions": "sugerencias de contenido si existen o vacío",
      "estimated_revenue": número estimado en USD basado en el tipo de campaña
    }
  ]
}
Reglas:
- Si no encuentras fechas exactas, infiere fechas razonables
- estimated_revenue debe ser un número entre 500 y 50000
- channels debe ser un array con al menos un valor válido
- objective debe ser uno de los valores permitidos
- Devuelve SOLO el JSON, sin explicaciones ni markdown
- content_suggestions máximo 100 caracteres, muy breve`;

  let res, data, text;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 2000 * attempt));
    res = await fetch(GEMINI_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt + '\n\nDatos:\n' + textoPlano }] }],
        generationConfig: { temperature: 0.3, maxOutputTokens: 16384 }
      })
    });
    if (res.ok) break;
    console.warn(`[GEMINI] Intento ${attempt + 1} fallido: ${res.status}`);
  }
  if (!res.ok) throw new Error(`Gemini error: ${res.status}`);
  data = await res.json();
  text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  console.log('[GEMINI RAW]', text);
  // Limpiar thinking tags de gemini-2.5-flash y markdown
  let clean = text
    .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
    .replace(/```json/g, '')
    .replace(/```/g, '')
    .trim();
  // Extraer solo el JSON si hay texto extra
  const jsonMatch = clean.match(/\{[\s\S]*\}/);
  if (jsonMatch) clean = jsonMatch[0];
  // Limpiar comillas tipográficas que rompen JSON.parse
  clean = clean
    .replace(/\u201c|\u201d|\u2018|\u2019/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
  return JSON.parse(clean);
}

// ─── Gemini: generar Golden Opportunities ────────────────────────────────────
async function generarGoldenOpportunities(entry) {
  const prompt = `Eres un experto en e-commerce y revenue optimization.
Dada esta campaña de marketing, genera 3 Golden Opportunities específicas y accionables.
Devuelve SOLO JSON puro sin markdown:
{
  "opportunities": [
    {
      "titulo": "título corto de la oportunidad",
      "segmento": "nombre del segmento (Champions, Activos, En Riesgo, Dormidos, etc.)",
      "descripcion": "máximo 15 palabras",
      "estimated_revenue": número en USD,
      "canal": "Email|SMS|Push|Web"
    }
  ],
  "total_estimated": número total en USD,
  "timing": "recomendación de cuándo lanzar (ej: 3 días antes)"
}

Campaña:
- Nombre: ${entry.concept}
- Objetivo: ${entry.objective}
- Período: ${entry.date_start} al ${entry.date_end}
- Oferta: ${entry.discounts}
- Canales: ${entry.channels?.join(', ')}

Genera oportunidades realistas y específicas para e-commerce latinoamericano.
Devuelve SOLO el JSON.`;

  const res = await fetch(GEMINI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: { temperature: 0.7, maxOutputTokens: 4096 }
    })
  });

  const data = await res.json();
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  console.log('[GEMINI RAW]', text);
  // Limpiar thinking tags de gemini-2.5-flash y markdown
  let clean = text
    .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
    .replace(/```json/g, '')
    .replace(/```/g, '')
    .trim();
  // Extraer solo el JSON si hay texto extra
  const jsonMatch = clean.match(/\{[\s\S]*\}/);
  if (jsonMatch) clean = jsonMatch[0];
  // Limpiar comillas tipográficas que rompen JSON.parse
  clean = clean
    .replace(/\u201c|\u201d|\u2018|\u2019/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
  return JSON.parse(clean);
}

// ─── Gemini: sugerencias proactivas ──────────────────────────────────────────
async function generarSugerenciasProactivas(campanasActivas) {
  const fechasUsadas = campanasActivas.flatMap(c => [c.date_start, c.date_end]);
  const prompt = `Eres un experto en e-commerce. Analiza qué fechas comerciales importantes NO están cubiertas y genera sugerencias.
Devuelve SOLO JSON puro:
{
  "sugerencias": [
    {
      "fecha_especial": "nombre de la fecha",
      "fecha": "YYYY-MM-DD",
      "dias_restantes": número,
      "motivo": "por qué es importante para e-commerce",
      "segmento_sugerido": "segmento recomendado",
      "estimated_revenue": número en USD,
      "urgencia": "alta|media|baja"
    }
  ]
}

Fechas especiales próximas: ${JSON.stringify(FECHAS_ESPECIALES)}
Campañas ya programadas (fechas): ${JSON.stringify(fechasUsadas)}

Solo incluye fechas que NO estén cubiertas y que sean en los próximos 90 días.
Máximo 3 sugerencias. Devuelve SOLO el JSON.`;

  const res = await fetch(GEMINI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: { temperature: 0.5, maxOutputTokens: 1024 }
    })
  });

  const data = await res.json();
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  console.log('[GEMINI RAW]', text);
  // Limpiar thinking tags de gemini-2.5-flash y markdown
  let clean = text
    .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
    .replace(/```json/g, '')
    .replace(/```/g, '')
    .trim();
  // Extraer solo el JSON si hay texto extra
  const jsonMatch = clean.match(/\{[\s\S]*\}/);
  if (jsonMatch) clean = jsonMatch[0];
  // Limpiar comillas tipográficas que rompen JSON.parse
  clean = clean
    .replace(/\u201c|\u201d|\u2018|\u2019/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
  return JSON.parse(clean);
}

// ─── Subcomponente: Badge objetivo ───────────────────────────────────────────
function ObjectiveBadge({ objective }) {
  return (
    <span className="cc-badge" style={{ background: OBJECTIVE_COLORS[objective] || '#6b7280' }}>
      {objective}
    </span>
  );
}

// ─── Subcomponente: Estado badge ─────────────────────────────────────────────
function EstadoBadge({ estado }) {
  const map = { activa: { label: 'Active', cls: 'cc-estado--activa' }, proxima: { label: 'Upcoming', cls: 'cc-estado--proxima' }, terminada: { label: 'Finished', cls: 'cc-estado--terminada' } };
  const { label, cls } = map[estado] || map.proxima;
  return <span className={`cc-estado ${cls}`}>{label}</span>;
}

// ─── Subcomponente: Tarjeta de campaña ───────────────────────────────────────
function CampanaCard({ entry, onVer, onEditar, onActivar }) {
  const estado = getEstado(entry);
  const revenue = entry.estimated_revenue ? `$${Number(entry.estimated_revenue).toLocaleString()}` : null;

  return (
    <div className={`cc-campana-card cc-campana-card--${estado}`}>
      <div className="cc-campana-top">
        <div className="cc-campana-info">
          <div className="cc-campana-title-row">
            <ObjectiveBadge objective={entry.objective} />
            <EstadoBadge estado={estado} />
            <span className="cc-campana-title">{entry.concept}</span>
          </div>
          <div className="cc-campana-meta">
            {formatDate(entry.date_start)} → {formatDate(entry.date_end)}
            {entry.channels?.length > 0 && (
              <span className="cc-campana-channels">
                · {entry.channels.join(' · ')}
              </span>
            )}
          </div>
        </div>
        <div className="cc-campana-actions">
          {revenue && (
            <div className="cc-revenue-pill">
              💰 {revenue} est.
            </div>
          )}
          <button className="cc-btn cc-btn--sm cc-btn--ghost" onClick={() => onVer(entry)}>Ver</button>
          <button className="cc-btn cc-btn--sm cc-btn--outline" onClick={() => onEditar(entry)}>Editar</button>
          {estado !== 'terminada' && (
            <button className="cc-btn cc-btn--sm cc-btn--primary" onClick={() => onActivar(entry)}>
              {estado === 'activa' ? '● Activa' : 'Activar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponente: Panel Ver campaña ────────────────────────────────────────
function VerCampanaPanel({ entry, onClose, onAskChat }) {
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await generarGoldenOpportunities(entry);
        if (!cancelled) setOpportunities(result);
      } catch (e) {
        if (!cancelled) setError('No se pudieron generar las oportunidades. Verifica la API key.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [entry]);

  return (
    <div className="cc-panel-overlay" onClick={onClose}>
      <div className="cc-panel" onClick={e => e.stopPropagation()}>
        <div className="cc-panel-header">
          <div>
            <div className="cc-panel-title-row">
              <ObjectiveBadge objective={entry.objective} />
              <h3>{entry.concept}</h3>
            </div>
            <p className="cc-panel-dates">{formatDate(entry.date_start)} → {formatDate(entry.date_end)}</p>
          </div>
          <button className="cc-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="cc-panel-body">
          {/* Detalles */}
          <div className="cc-panel-section">
            <div className="cc-detail-row"><span className="cc-detail-label">Offer</span><span>{entry.discounts}</span></div>
            <div className="cc-detail-row"><span className="cc-detail-label">Channels</span><div className="cc-chips">{entry.channels?.map(c => <span key={c} className="cc-chip">{c}</span>)}</div></div>
            {entry.promo_code && <div className="cc-detail-row"><span className="cc-detail-label">Code</span><code className="cc-code">{entry.promo_code}</code></div>}
            {entry.content_suggestions && <div className="cc-detail-row"><span className="cc-detail-label">Suggestions</span><span>{entry.content_suggestions}</span></div>}
          </div>

          {/* Golden Opportunities */}
          <div className="cc-panel-section">
            <div className="cc-section-title">
              <span>⚡ Golden Opportunities</span>
              {opportunities?.total_estimated && (
                <span className="cc-total-revenue">Total est: ${Number(opportunities.total_estimated).toLocaleString()}</span>
              )}
            </div>

            {loading && <div className="cc-loading-ai">✨ Generando con Gemini AI...</div>}
            {error && <div className="cc-error">{error}</div>}

            {opportunities?.opportunities?.map((op, i) => (
              <div key={i} className="cc-opportunity-card">
                <div className="cc-op-header">
                  <div>
                    <div className="cc-op-title">{op.titulo}</div>
                    <div className="cc-op-segment">Segmento: <strong>{op.segmento}</strong> · {op.canal}</div>
                    <div className="cc-op-desc">{op.descripcion}</div>
                  </div>
                  <div className="cc-op-revenue">${Number(op.estimated_revenue).toLocaleString()}</div>
                </div>
              </div>
            ))}

            {opportunities?.timing && (
              <div className="cc-timing-note">🕐 {opportunities.timing}</div>
            )}
          </div>
        </div>

        <div className="cc-panel-footer">
          <button
            className="cc-btn cc-btn--primary cc-btn--full"
            onClick={() => onAskChat(`Analyze the campaign "${entry.concept}" from ${entry.date_start} to ${entry.date_end}. Objective: ${entry.objective}. Offer: ${entry.discounts}. How do I maximize revenue with my customer segments?`)}
          >
            💬 Ask the Chat
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponente: Uploader Excel ───────────────────────────────────────────
function ExcelUploader({ onParsed, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const handleFile = async (f) => {
    setFile(f);
    setError('');
    setLoading(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const textoPlano = rows
        .filter(row => row.some(c => c !== '' && c !== null))
        .slice(0, 40)
        .map(row => row.map(cell => {
          if (typeof cell === 'number' && cell > 40000 && cell < 60000) {
            return new Date((cell - 25569) * 86400 * 1000).toLocaleDateString('es-CO');
          }
          return String(cell).trim();
        }).filter(c => c !== '').join(' | '))
        .filter(line => line.length > 5)
        .join('\n');
      const resultado = await interpretarExcelConGemini(textoPlano);
      setPreview(resultado);
    } catch (e) {
      setError('Could not read the file. Make sure it is a valid Excel file.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  return (
    <div className="cc-panel-overlay" onClick={onClose}>
      <div className="cc-panel cc-panel--wide" onClick={e => e.stopPropagation()}>
        <div className="cc-panel-header">
          <div>
            <h3>Upload from Excel</h3>
            <p className="cc-panel-dates">Gemini AI will read your file automatically</p>
          </div>
          <button className="cc-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="cc-panel-body">
          {!preview ? (
            <>
              <div
                className="cc-dropzone"
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                />
                {loading ? (
                  <div className="cc-loading-ai">✨ Gemini is reading your file...</div>
                ) : (
                  <>
                    <div className="cc-dropzone-icon">📊</div>
                    <div className="cc-dropzone-text">Drag your Excel file here or click to select</div>
                    <div className="cc-dropzone-sub">Formats: .xlsx, .xls, .csv · Gemini reads any format</div>
                  </>
                )}
              </div>
              {error && <div className="cc-error">{error}</div>}
            </>
          ) : (
            <>
              <div className="cc-preview-title">
                ✅ Gemini found <strong>{preview.entries?.length} campaigns</strong> — "{preview.calendar_name}"
              </div>
              <div className="cc-preview-list">
                {preview.entries?.map((e, i) => (
                  <div key={i} className="cc-preview-entry">
                    <ObjectiveBadge objective={e.objective} />
                    <span className="cc-preview-concept">{e.concept}</span>
                    <span className="cc-preview-dates">{formatDate(e.date_start)} → {formatDate(e.date_end)}</span>
                    {e.estimated_revenue && <span className="cc-preview-rev">${Number(e.estimated_revenue).toLocaleString()}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {preview && (
          <div className="cc-panel-footer">
            <button className="cc-btn cc-btn--outline" onClick={() => setPreview(null)}>Volver a cargar</button>
            <button className="cc-btn cc-btn--primary" onClick={() => onParsed(preview)}>Guardar calendario</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subcomponente: Sugerencias proactivas ───────────────────────────────────
function SugerenciasProactivas({ campanasActivas, onCrear }) {
  const [sugerencias, setSugerencias] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) return <div className="cc-sugerencias-loading">✨ Analyzing proactive opportunities...</div>;
  if (!sugerencias.length) return null;

  return (
    <div className="cc-sugerencias">
      <div className="cc-sugerencias-title">💡 Opportunities detected without a campaign</div>
      <div className="cc-sugerencias-list">
        {sugerencias.map((s, i) => (
          <div key={i} className={`cc-sugerencia-card cc-sugerencia--${s.urgencia}`}>
            <div className="cc-sugerencia-info">
              <div className="cc-sugerencia-nombre">{s.fecha_especial}</div>
              <div className="cc-sugerencia-meta">
                {formatDate(s.fecha)} · {s.dias_restantes} days · {s.motivo}
              </div>
              <div className="cc-sugerencia-segmento">Suggested segment: <strong>{s.segmento_sugerido}</strong></div>
            </div>
            <div className="cc-sugerencia-right">
              <div className="cc-sugerencia-revenue">${Number(s.estimated_revenue).toLocaleString()} est.</div>
              <button className="cc-btn cc-btn--sm cc-btn--primary" onClick={() => onCrear(s)}>+ Create</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CalendarioCampanas({ onAskChat }) {
  const [calendarios, setCalendarios] = useState([
    {
      id: 'cal_demo',
      name: 'Q4 2025 — Black Friday Season',
      created_at: '2025-10-01',
      entries: [
        {
          id: 'e1', date_start: '2026-05-25', date_end: '2026-05-30',
          objective: 'Sales', concept: 'Black Friday — VIP Email',
          discounts: 'Buy 3 get 1 free + Free shipping over $55',
          channels: ['Email', 'Web'], promo_code: 'VIP2025',
          content_suggestions: '3 emails: Monday launch, Wednesday reminder, Friday last chance',
          estimated_revenue: 9100,
        },
        {
          id: 'e2', date_start: '2026-06-01', date_end: '2026-06-10',
          objective: 'Retention', concept: 'Cyber Monday — Clientes frecuentes',
          discounts: '20% off en toda la tienda',
          channels: ['Email', 'SMS'], promo_code: 'CYBER20',
          content_suggestions: '', estimated_revenue: 4200,
        },
      ],
    },
  ]);

  const [view, setView] = useState('list'); // list | nuevo
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [calendarName, setCalendarName] = useState('');
  const [entries, setEntries] = useState([{
    date_start: '', date_end: '', objective: 'Sales',
    concept: '', discounts: '', channels: [], promo_code: '', content_suggestions: '',
  }]);

  const todasLasEntradas = calendarios.flatMap(c => c.entries);

  const handleExcelParsed = (parsed) => {
    const nuevoCalendario = {
      id: `cal_${Date.now()}`,
      name: parsed.calendar_name,
      created_at: new Date().toISOString().split('T')[0],
      entries: parsed.entries.map((e, i) => ({ ...e, id: `e_${Date.now()}_${i}` })),
    };
    setCalendarios(prev => [nuevoCalendario, ...prev]);
    setShowUploader(false);
  };

  const handleGuardarManual = () => {
    if (!calendarName.trim()) return;
    const nuevo = {
      id: `cal_${Date.now()}`,
      name: calendarName,
      created_at: new Date().toISOString().split('T')[0],
      entries: entries.map((e, i) => ({ ...e, id: `e_${Date.now()}_${i}` })),
    };
    setCalendarios(prev => [nuevo, ...prev]);
    setView('list');
  };

  const handleCrearDesdeSugerencia = (sugerencia) => {
    setCalendarName(`Campaign ${sugerencia.fecha_especial}`);
    setEntries([{
      date_start: sugerencia.fecha,
      date_end: sugerencia.fecha,
      objective: 'Sales',
      concept: sugerencia.fecha_especial,
      discounts: '',
      channels: ['Email'],
      promo_code: '',
      content_suggestions: sugerencia.motivo,
    }]);
    setView('nuevo');
  };

  return (
    <div className="cc-root">
      {/* Header */}
      <div className="cc-header">
        <div className="cc-header-left">
          <div className="cc-header-icon">📅</div>
          <div>
            <h1>Campaign Calendar</h1>
            <p>Plan your campaigns and activate Golden Opportunities with AI</p>
          </div>
        </div>
        {view === 'list' && (
          <div className="cc-header-actions">
            <button className="cc-btn cc-btn--outline" onClick={() => setShowUploader(true)}>
              📊 Upload Excel
            </button>
            <button className="cc-btn cc-btn--primary" onClick={() => setView('nuevo')}>
              + New campaign
            </button>
          </div>
        )}
        {view === 'nuevo' && (
          <button className="cc-btn cc-btn--ghost" onClick={() => setView('list')}>← Back</button>
        )}
      </div>

      {/* Vista: Lista */}
      {view === 'list' && (
        <div className="cc-list-view">
          {/* Sugerencias proactivas */}
          <SugerenciasProactivas
            campanasActivas={todasLasEntradas}
            onCrear={handleCrearDesdeSugerencia}
          />

          {/* Calendarios */}
          {calendarios.length === 0 ? (
            <div className="cc-empty">
              <div className="cc-empty-icon">📅</div>
              <h3>No campaigns yet</h3>
              <p>Upload an Excel file or create your first campaign manually.</p>
            </div>
          ) : (
            calendarios.map(cal => {
              // Agrupar entradas por mes
              const porMes = {};
              cal.entries.forEach(entry => {
                const fecha = new Date(entry.date_start + 'T00:00:00');
                const key = fecha.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                if (!porMes[key]) porMes[key] = [];
                porMes[key].push(entry);
              });

              return (
                <div key={cal.id} className="cc-calendario-grupo">
                  <div className="cc-calendario-nombre">
                    <span>{cal.name}</span>
                    <span className="cc-calendario-meta">{cal.entries.length} campaign{cal.entries.length !== 1 ? 's' : ''}</span>
                  </div>
                  {Object.entries(porMes).map(([mes, entries]) => (
                    <div key={mes} className="cc-mes-grupo">
                      <div className="cc-mes-label">📅 {mes.charAt(0).toUpperCase() + mes.slice(1)}</div>
                      <div className="cc-campanas-list">
                        {entries.map(entry => (
                          <CampanaCard
                            key={entry.id}
                            entry={entry}
                            onVer={setSelectedEntry}
                            onEditar={() => {}}
                            onActivar={() => {}}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Vista: Nuevo manual */}
      {view === 'nuevo' && (
        <div className="cc-nuevo-view">
          <div className="cc-form-field">
            <label><strong>Calendar name</strong></label>
            <input
              type="text"
              placeholder="Ej: Q4 2025 — Black Friday Season"
              value={calendarName}
              onChange={e => setCalendarName(e.target.value)}
            />
          </div>

          <div className="cc-form-divider" />
          <div className="cc-form-section-title">Campaigns</div>

          {entries.map((entry, idx) => (
            <div key={idx} className="cc-form-entry">
              <div className="cc-form-entry-header">
                <span>Campaign {idx + 1}{entry.concept ? `: ${entry.concept}` : ''}</span>
                {entries.length > 1 && (
                  <button className="cc-remove-btn" onClick={() => setEntries(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                )}
              </div>
              <div className="cc-form-entry-body">
                <div className="cc-form-row-2">
                  <div className="cc-form-field">
                    <label>Start date</label>
                    <input type="date" value={entry.date_start} onChange={e => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, date_start: e.target.value } : en))} />
                  </div>
                  <div className="cc-form-field">
                    <label>End date</label>
                    <input type="date" value={entry.date_end} onChange={e => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, date_end: e.target.value } : en))} />
                  </div>
                </div>
                <div className="cc-form-field">
                  <label>Objective</label>
                  <select value={entry.objective} onChange={e => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, objective: e.target.value } : en))}>
                    {OBJECTIVES.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="cc-form-field">
                  <label>Concept</label>
                  <input type="text" placeholder="E.g.: Pre-Black Friday — VIP Email" value={entry.concept} onChange={e => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, concept: e.target.value } : en))} />
                </div>
                <div className="cc-form-field">
                  <label>Discount / Offer</label>
                  <textarea rows={2} placeholder="E.g.: 20% off store-wide" value={entry.discounts} onChange={e => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, discounts: e.target.value } : en))} />
                </div>
                <div className="cc-form-field">
                  <label>Channels</label>
                  <div className="cc-chips-selector">
                    {CHANNELS.map(ch => (
                      <button
                        key={ch}
                        type="button"
                        className={`cc-chip-btn ${entry.channels.includes(ch) ? 'cc-chip-btn--selected' : ''}`}
                        onClick={() => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, channels: en.channels.includes(ch) ? en.channels.filter(c => c !== ch) : [...en.channels, ch] } : en))}
                      >
                        {ch}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="cc-form-field">
                  <label>Promo code <span className="cc-optional">(optional)</span></label>
                  <input type="text" placeholder="E.g.: VIP2025" value={entry.promo_code} onChange={e => setEntries(prev => prev.map((en, i) => i === idx ? { ...en, promo_code: e.target.value } : en))} />
                </div>
              </div>
            </div>
          ))}

          <button className="cc-btn--dashed" onClick={() => setEntries(prev => [...prev, { date_start: '', date_end: '', objective: 'Sales', concept: '', discounts: '', channels: [], promo_code: '', content_suggestions: '' }])}>
            + Add another campaign
          </button>

          <div className="cc-form-divider" />
          <div className="cc-form-actions">
            <button className="cc-btn cc-btn--outline" onClick={() => setView('list')}>Cancel</button>
            <button className="cc-btn cc-btn--primary" onClick={handleGuardarManual}>Save calendar</button>
          </div>
        </div>
      )}

      {/* Panel: Ver campaña */}
      {selectedEntry && (
        <VerCampanaPanel
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onAskChat={(prompt) => {
            setSelectedEntry(null);
            onAskChat && onAskChat(prompt);
          }}
        />
      )}

      {/* Panel: Excel uploader */}
      {showUploader && (
        <ExcelUploader
          onParsed={handleExcelParsed}
          onClose={() => setShowUploader(false)}
        />
      )}
    </div>
  );
}
